import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import {
  ClientTrialService,
  ToastMessageService,
  TrialService,
} from '@fitness-force/api';
import { Status } from '@fitness-force/enum';
import { AppErrors } from '@fitness-force/models';
import type {
  Appointment,
  Class,
  CreateClientTrial,
  TrialBookingRequest,
  TrialMaster,
} from '@fitness-force/models';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';

import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';

import { BehaviorSubject } from 'rxjs';
import { CalendarComponent } from '../calendar/calendar.component';
import { ErrorsComponent } from '@fitness-force/errors';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';

@Component({
  selector: 'client-trial-studio',
  templateUrl: './client-trial-studio.component.html',
  styleUrls: ['./client-trial-studio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    DialogModule,
    MessageModule,
    SkeletonModule,
    StyleClassModule,
    CalendarComponent,
    ErrorsComponent,
    TranslateModule,
    AsyncPipe,
  ],
})
export class ClientTrialStudioComponent implements OnInit {
  private clientTrialService = inject(ClientTrialService);
  private trialMasterService = inject(TrialService);
  translate = inject(TranslateService);
  private toastMessageService = inject(ToastMessageService);
  private confirmationService = inject(ConfirmationService);

  readonly tenantId = input<number>(0);
  readonly clientId = input<number>(0);
  readonly visible = input<boolean>(false);
  readonly showCreateTrialButton = input<boolean>(false);
  readonly closeEvent = output<any>();
  readonly createTrial = output<boolean>();

  trialMaster: TrialMaster | undefined;
  minimumDate = new Date();
  clientTrialForm: UntypedFormGroup = new UntypedFormGroup({});
  showDaysBased: boolean = false;
  showClassBased: boolean = false;

  eventObject: any;
  appointmentObject: Appointment | undefined;
  classObject: Class | undefined;

  errorMessages = [] as any;
  error = new BehaviorSubject(null as AppErrors | null);
  showErrorPopup$ = new BehaviorSubject(false);
  private isConfirmDialogOpen = false;

  ngOnInit(): void {
    this.getTrialMaster();
  }

  getTrialMaster() {
    this.trialMasterService.getTrials(this.tenantId()).subscribe({
      next: (response: any) => {
        if (response && response.items) {
          let trialMasters = response.items as TrialMaster[];
          this.trialMaster = trialMasters.find(
            (o) =>
              o.status == Status.Active ||
              o.status.toString() == Status[Status.Active],
          );
        }
      },
      error: (error) => {
        this.handleError(error);
        this.trialMaster = undefined;
      },
    });
  }

  getEventData(event: any) {
    this.classObject = event.classData;
    this.eventObject = event.eventData;
    this.CreateClientTrial(false);
  }

  CreateClientTrial(ByPassLimit: boolean) {
    let trialBookingRequest: TrialBookingRequest = {} as TrialBookingRequest;
    trialBookingRequest.client_id = this.clientId();
    trialBookingRequest.trial_mst_id = this.trialMaster!.id;
    this.clientTrialService
      .CreateClientTrialWithClassBooking(
        this.tenantId(),
        this.classObject!.id,
        ByPassLimit,
        trialBookingRequest,
      )
      .subscribe({
        next: async (res: any) => {
          this.closePopup(true);
          await this.timeout(1000);
          this.translate
            .get('CLIENT_TRIAL.MSG_SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        },
        error: (error: any) => {
          this.handleError(error);
        },
      });
  }

  closePopup(reloadData: boolean) {
    this.closeEvent.emit({ closePopup: true, reloadData: false });
    this.closeEvent.emit({ closePopup: true, reloadData: reloadData });
  }

  handleError(error: any) {
    const apiErrors = error?.originalError?.error;
    if (!Array.isArray(apiErrors) || apiErrors.length === 0) {
      this.showGenericError(error);
      return;
    }

    const hasSessionDoesNotExist = apiErrors.some(
      (e: { errorCode?: string }) =>
        e.errorCode?.toLowerCase() === 'sessiondoesnotexist',
    );
    if (hasSessionDoesNotExist) {
      this.confirm();
      return;
    }

    this.showGenericError(error);
  }

  private showGenericError(error: any): void {
    this.error.next(error);
    this.showErrorPopup$.next(true);
  }

  HideErrorPopup() {
    this.showErrorPopup$?.next(false);
    this.errorMessages = [];
  }

  confirm(): void {
    if (this.isConfirmDialogOpen) {
      return;
    }

    this.translate
      .get([
        'CLIENT_TRIAL.COMFIRM1',
        'CLIENT_TRIAL.COMFIRM2',
        'CLIENT_TRIAL.CONTINUE',
        'CLIENT_TRIAL.CANCEL',
      ])
      .subscribe((translations) => {
        this.isConfirmDialogOpen = true;
        this.confirmationService.confirm({
          message: translations['CLIENT_TRIAL.COMFIRM1'],
          acceptLabel: translations['CLIENT_TRIAL.CONTINUE'],
          rejectLabel: translations['CLIENT_TRIAL.CANCEL'],
          icon: 'fa fa-exclamation-triangle',
          rejectButtonStyleClass: 'p-button-text',
          accept: () => {
            this.resetConfirmDialogState();
            this.CreateClientTrial(true);
          },
          reject: (type: ConfirmEventType) => {
            this.resetConfirmDialogState();
            if (
              type === ConfirmEventType.REJECT ||
              type === ConfirmEventType.CANCEL
            ) {
              this.confirmationService.close();
            }
          },
        });
      });
  }

  private resetConfirmDialogState(): void {
    this.isConfirmDialogOpen = false;
  }

  createTrialConfirm(): void {
    if (this.isConfirmDialogOpen) {
      return;
    }

    this.translate
      .get([
        'CLIENT_TRIAL.CONFIRM_TRIAL',
        'CLIENT_TRIAL.CONTINUE',
        'CLIENT_TRIAL.CANCEL',
      ])
      .subscribe((translations) => {
        this.isConfirmDialogOpen = true;
        this.confirmationService.confirm({
          message: translations['CLIENT_TRIAL.CONFIRM_TRIAL'],
          acceptLabel: translations['CLIENT_TRIAL.CONTINUE'],
          rejectLabel: translations['CLIENT_TRIAL.CANCEL'],
          icon: 'fa fa-exclamation-triangle',
          rejectButtonStyleClass: 'p-button-text',
          accept: () => {
            this.resetConfirmDialogState();
            this.confirmationService.close();
            this.createClientTrials();
          },
          reject: (type: ConfirmEventType) => {
            this.resetConfirmDialogState();
            if (
              type === ConfirmEventType.REJECT ||
              type === ConfirmEventType.CANCEL
            ) {
              this.confirmationService.close();
            }
          },
        });
      });
  }

  createClientTrials() {
    let createClientTrial: CreateClientTrial = {} as CreateClientTrial;
    createClientTrial.start_date = new Date(
      new Date().setHours(0, 0, 0, 0),
    ).toISOString();
    ((createClientTrial.opportunity_id = 0),
      (createClientTrial.owner_id = 0),
      (createClientTrial.trial_master_id = this.trialMaster!.id),
      this.clientTrialService
        .CreateClientTrial(this.tenantId(), this.clientId(), createClientTrial)
        .subscribe({
          next: (res: any) => {
            this.translate
              .get('CLIENT_TRIAL.MSG_SUCCESS')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.createTrial.emit(true);
            this.closePopup(true);
          },
          error: (error: any) => {
            this.handleError(error);
          },
        }));
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  closeCreateTrialEvent(event: any) {
    this.showDaysBased = false;
  }
}
